import { Box, Divider, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import DownloadIcon from "../../../../assets/TailoredGeneration/icons/tg-dataset-download-icon.svg";
import ZipSvg from "../../../../assets/TailoredGeneration/icons/zip-icon.svg";
import BackButton from "../../../../components/common/BackButton/BackButton.tsx";
import BestPractisesButton from "../../../../components/common/BestPractisesButton/BestPractisesButton.tsx";
import BriaButton from "../../../../components/common/BriaButton/BriaButton";
import BriaButtonGroup from "../../../../components/common/BriaButtonGroup/BriaButtonGroup";
import LoadingPlaceholder from "../../../../components/common/LoadingPlaceholder/LoadingPlaceholder";
import RowJsonStructure from "../../../../components/common/RowJsonStructure/RowJsonStructure.tsx";
import RouterConstants from "../../../../constants/RouterConstants";
import useSecureNavigate from "../../../../hooks/useSecureNavigate";
import { useAppStore } from "../../../../hooks/useStores";
import TgImagesPreviewThumbstrip from "../../Models/FiboCreate/components/TgImagesPreviewThumbstrip/TgImagesPreviewThumbstrip.tsx";
import { TgDataset, defaultDataset } from "../store/tg-dataset-store";
import styles from "./NewTgDatasetViewer.module.scss";

type TgAdvancedDatasetViewerProps = {
	embeddedView?: boolean;
};

const NewTgDatasetViewer = ({ embeddedView }: TgAdvancedDatasetViewerProps) => {
	const { t } = useTranslation("tg", { keyPrefix: "datasets.newDatasetViewer" });
	const navigate = useSecureNavigate();
	const { projectId, datasetId } = useParams();
	const { tgStore } = useAppStore();
	const { tgDatasetStore } = tgStore;
	const [dataset, setDataset] = useState<TgDataset>(defaultDataset);
	const [downloadUrl, setDownloadUrl] = useState<string | undefined>();
	const [captionsUrl, setCaptionsUrl] = useState<string | undefined>();
	const [loadingUrls, setLoadingUrls] = useState<boolean>(false);
	const [loadingDownload, setLoadingDownload] = useState<boolean>(false);

	useEffect(() => {
		(async () => {
			try {
				if (datasetId) {
					const ds = await tgDatasetStore.getDataset(Number(datasetId), 200);
					setDataset(ds);
					if (ds.upload_type === "advanced") {
						loadDownloadUrls(ds.id);
					}
				}
			} catch (err: any) {
				if (err.response?.status === 404) {
					navigate(RouterConstants.NEW_TG.fullPath);
				}
			}
		})();

		return () => {
			window.history.replaceState({}, "");
		};
	}, []);

	const loadDownloadUrls = async (_datasetId: number) => {
		setLoadingUrls(true);
		try {
			const urls = await tgDatasetStore.getAdvancedDatasetDownloadUrls(_datasetId);
			setDownloadUrl(urls.download_url);
			setCaptionsUrl(urls.captions_url);
		} finally {
			setLoadingUrls(false);
		}
	};

	const primaryButtonClick = () => {
		const base = RouterConstants.NEW_TG.PROJECTS.fullPath;
		navigate(`${base}/${projectId}/${RouterConstants.NEW_TG.MODELS.createPath}`);
	};

	const downloadDataset = async () => {
		// Advanced datasets
		if (dataset.upload_type === "advanced") {
			if (downloadUrl) {
				window.open(downloadUrl, "_blank");
			}
			return;
		}

		// Basic datasets
		if (!dataset.id) return;
		setLoadingDownload(true);
		try {
			await tgDatasetStore.downloadDatasetZip(String(dataset.id), dataset.name);
		} finally {
			setLoadingDownload(false);
		}
	};

	const downloadCaptions = async () => {
		if (captionsUrl) {
			window.open(captionsUrl, "_blank");
		}
	};

	const shortDescription = useMemo(() => {
		if (!dataset.visual_schema) return "";
		try {
			const obj = JSON.parse(dataset.visual_schema);
			return typeof obj?.short_description === "string" ? obj.short_description : "";
		} catch {
			return "";
		}
	}, [dataset.visual_schema]);

	const images = useMemo(() => (dataset.images || []).slice(0, 200), [dataset.images]);
	const [selectedIndex, setSelectedIndex] = useState<number>(0);
	const selected = images[selectedIndex];
	const canPrev = selectedIndex > 0;
	const canNext = selectedIndex < Math.max(images.length - 1, 0);
	const goPrev = () => setSelectedIndex((i) => (i > 0 ? i - 1 : i));
	const goNext = () => setSelectedIndex((i) => (i < images.length - 1 ? i + 1 : i));
	const buildPaginationItems = (total: number, idx: number): (number | "...")[] => {
		if (total <= 4) return Array.from({ length: total }, (_, i) => i);
		if (idx <= 1) return [0, 1, 2, "...", total - 1];
		if (idx >= total - 2) return [0, "...", Math.max(total - 3, 0), total - 2, total - 1];
		return [idx - 1, idx, idx + 1, "...", total - 1];
	};
	const paginationItems = useMemo<(number | "...")[]>(
		() => buildPaginationItems(images.length, selectedIndex),
		[images.length, selectedIndex],
	);
	const selectedShortDescription = useMemo(() => {
		try {
			const vs = selected?.caption || "";
			if (!vs) return "";
			const obj = JSON.parse(vs);
			return typeof obj?.short_description === "string" ? obj.short_description : "";
		} catch {
			return "";
		}
	}, [selected?.caption]);

	useEffect(() => {
		if (selectedIndex >= images.length && images.length > 0) {
			setSelectedIndex(0);
		}
	}, [images.length, selectedIndex]);

	return (
		<LoadingPlaceholder className={styles.loadingPlaceholder} isLoading={tgDatasetStore.loadingDataset}>
			<Box className={`${styles.viewerContainer} ${embeddedView ? "" : styles.fullScreenView}`}>
				<Box className={styles.header}>
					<Box className={styles.headerLeft}>
						<BackButton hideText />
						<Typography className={styles.name}>{t("title")}</Typography>
					</Box>
					<BriaButtonGroup>
						<BestPractisesButton hideIcon buttonType={"secondarySmall"} />
						<BriaButton buttonType="primaryMedium" onClick={primaryButtonClick}>
							{t("primaryButton")}
						</BriaButton>
					</BriaButtonGroup>
				</Box>
				<Divider className={styles.sectionDivider} />

				{/* Dataset Summary Section */}
				<Box className={styles.datasetSummary}>
					<Typography className={styles.sectionTitle}>{t("datasetSummary.title")}</Typography>
					<Box
						className={`${styles.summaryGrid} ${
							dataset.upload_type === "advanced" ? styles.advancedSummaryGrid : ""
						}`}
					>
						<Box className={styles.summaryCard}>
							<Typography className={styles.summaryLabel}>{t("datasetSummary.datasetName")}</Typography>
							<Typography className={styles.summaryValue}>{dataset.name}</Typography>
						</Box>
						<Box className={styles.summaryCard}>
							<Typography className={styles.summaryLabel}>{t("datasetSummary.datasetFile")}</Typography>
							<Box className={styles.downloadSection}>
								<Box className={styles.zipTextContainer}>
									<img src={ZipSvg} alt={"zip"} />
									<Typography className={styles.summaryValue}>
										{`Zip File "${dataset.upload_type === "advanced" ? "original" : dataset.name}.zip"`}
									</Typography>
								</Box>
								<BriaButton
									buttonType="secondarySmall"
									loading={loadingUrls || loadingDownload}
									disabled={
										(dataset.upload_type === "advanced" && !downloadUrl) ||
										loadingUrls ||
										loadingDownload
									}
									onClick={downloadDataset}
								>
									<Box className={styles.downloadIcon}>
										<img src={DownloadIcon} alt={"zip"} />
									</Box>
									{t("datasetSummary.download")}
								</BriaButton>
							</Box>
						</Box>
						{dataset.upload_type === "advanced" && (
							<Box className={styles.summaryCard}>
								<Typography className={styles.summaryLabel}>
									{t("datasetSummary.captionFile")}
								</Typography>
								<Box className={styles.downloadSection}>
									<Box className={styles.zipTextContainer}>
										<img src={ZipSvg} alt={"zip"} />
										<Typography className={styles.summaryValue}>
											{`Zip File "captions.zip"`}
										</Typography>
									</Box>
									<BriaButton
										buttonType="secondarySmall"
										loading={loadingUrls || loadingDownload}
										disabled={
											(dataset.upload_type === "advanced" && !captionsUrl) ||
											loadingUrls ||
											loadingDownload
										}
										onClick={downloadCaptions}
									>
										<Box className={styles.downloadIcon}>
											<img src={DownloadIcon} alt={"zip"} />
										</Box>
										{t("datasetSummary.download")}
									</BriaButton>
								</Box>
							</Box>
						)}
					</Box>
					<Box className={styles.summaryGridBottom}>
						<Box className={styles.summaryCard}>
							<Typography className={styles.summaryLabel}>
								{t("datasetSummary.datasetVisualSchemaTitle")}
							</Typography>
							<Typography className={styles.summaryValueSchema}>{shortDescription}</Typography>
							<Divider className={styles.divider} />
							<RowJsonStructure
								title={t("datasetSummary.viewRawJsonTitle")}
								json={dataset.visual_schema || "{}"}
								hideUpload
								hideDownload
								hideTooltip
							/>
						</Box>
					</Box>
				</Box>

				<Divider className={styles.sectionDividerLight} />

				<Box className={styles.bottomSection}>
					<Box className={styles.bottomLeft}>
						<Typography className={styles.imagesTitle}>{t("datasetImagesView.title")}</Typography>
						<Box className={styles.captionsBox}>
							<Typography className={styles.capTitle}>
								{t("datasetImagesView.captionsSection.title")}
							</Typography>
							<Typography className={styles.capSubtitle}>
								{t("datasetImagesView.captionsSection.subtitle")}
							</Typography>
							<Typography className={styles.shortDesc}>{selectedShortDescription}</Typography>
							<Divider className={styles.dividerNoMargin} />
							<Box className={styles.bottomJsonStructureContainer}>
								<RowJsonStructure
									json={selected?.caption || "{}"}
									hideUpload
									hideDownload
									hideTooltip
								/>
							</Box>
							<Divider className={styles.dividerNoMargin} />
						</Box>
					</Box>
					<Box className={styles.bottomRight}>
						<TgImagesPreviewThumbstrip
							images={images}
							selectedIndex={selectedIndex}
							setSelectedIndex={setSelectedIndex}
							canPrev={canPrev}
							canNext={canNext}
							goPrev={goPrev}
							goNext={goNext}
							paginationItems={paginationItems}
							isLoadingCaptions={false}
							prevLabel={t("datasetImagesView.previousLabel")}
							nextLabel={t("datasetImagesView.nextLabel")}
							showThumbLoaders={false}
						/>
					</Box>
				</Box>
			</Box>
		</LoadingPlaceholder>
	);
};
export default observer(NewTgDatasetViewer);
